import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import toastUtils from "../../utils/toastUtils";

import { i18n } from "../../translate/i18n";
import api, { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  // REMOVIDO: Interceptors duplicados
  // Os interceptors já estão configurados em services/api.js

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    console.log('🔄 Iniciando verificação de autenticação...');

    (async () => {
      try {
        if (token) {
          console.log('✅ Token encontrado no localStorage');
          // Configurar o token e verificar se é válido fazendo uma chamada para /auth/me
          api.defaults.headers.Authorization = `Bearer ${JSON.parse(token)}`;

          // Tentar buscar os dados do usuário para validar o token
          const { data } = await api.get("/auth/me");

          // Safe destructuring for user data
          if (data && (data.user || data.id)) {
            console.log('✅ Usuário autenticado:', data.user?.name || data.name);
            setUser(data.user || data);
            setIsAuth(true);
          } else {
            console.warn('❌ Dados de usuário inválidos:', data);
            throw new Error('Invalid user data structure');
          }
        } else {
          console.log('❌ Nenhum token encontrado no localStorage');
          setIsAuth(false);
          setUser({});
        }
      } catch (err) {
        console.log("❌ Token inválido no localStorage:", err);
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        setUser({});
      } finally {
        // SEMPRE definir loading como false, independente do resultado
        console.log('✅ Verificação de autenticação concluída');
        setLoading(false);
      }
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);

      // Verificar se o usuário tem companyId válido
      if (!user.companyId) {
        console.warn("Usuário sem companyId definido:", user);
        return;
      }
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket com tratamento de erro melhorado
      try {
        const socketInstance = socketConnection({ user: {
          companyId: user.companyId,
          id: user.id }
        });
      
        if (socketInstance) {
          setSocket(socketInstance);

          // Aguardar um pouco para garantir que o socket está configurado
          setTimeout(() => {
            const eventName = `company-${user.companyId}-user`;

            const handleUserUpdate = (data) => {
              if (data.action === "update" && data.user.id === user.id) {
                setUser(data.user);
              }
            };

            // Verificar se o socket tem o método 'on'
            if (socketInstance && typeof socketInstance.on === 'function') {
              try {
                socketInstance.on(eventName, handleUserUpdate);
                listenersRef.current.add(eventName);
                console.log(`Listener adicionado para: ${eventName}`);
              } catch (error) {
                console.error("Erro ao adicionar listener ao socket:", error);
                setSocket(null);
              }
            } else {
              console.error("Socket instance não tem método 'on'", socketInstance);
              setSocket(null);
            }
          }, 100);
        } else {
          console.warn("Socket instance é null - aplicação funcionará sem socket");
          setSocket(null);
        }
      } catch (socketError) {
        console.error("Erro ao criar socket - possível problema de conectividade:", socketError);
        // Não definir socket em caso de erro, permitindo que a aplicação continue sem socket
        setSocket(null);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect removido - dados do usuário já são buscados na inicialização

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      // IMPORTANTE: Usar openApi para rotas públicas (sem token)
      const { data } = await openApi.post("/auth/login", userData);

      // Safe destructuring for login response
      if (!data || !data.user || !data.token) {
        throw new Error('Invalid login response structure');
      }

      const { user, token } = data;
      const company = user?.company || {};

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      var diff = moment(dueDate).diff(moment(moment()).format());
      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${token}`;
        setUser(user);
        setIsAuth(true);
        toastUtils.success(i18n.t("auth.toasts.success"), "Login Realizado");

        if (Math.round(dias) < 5) {
          toastUtils.warning(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `,
            "Assinatura Expirando"
          );
        }

        // Redirecionar baseado no perfil do usuário
        if (user && user.profile === "admin") {
          history.push("/");  // Dashboard para admin
        } else {
          history.push("/tickets");  // Tickets para outros usuários
        }
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    setUser,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;